<!-- StatsConfigMenu.vue -->
<template>
	<v-menu v-model="menuOpen" location="bottom start" :close-on-content-click="false">
		<template #activator="{ props: menuProps }">
			<span v-bind="menuProps" class="cursor-pointer d-inline-flex">
				<v-badge :content="committedSelection.length" :model-value="committedSelection.length > 0" color="primary-green" class="mt-2">
					<v-icon size="18" color="grey"> mdi-cog </v-icon>
				</v-badge>
			</span>
		</template>
		<v-card min-width="280">
			<v-card-text class="pa-0">
				<v-checkbox
					v-model="isAllSelected"
					:indeterminate="isIndeterminate"
					density="compact"
					hide-details
					color="primary-green"
					class="px-2 w-100 font-weight-bold"
				>
					<template v-slot:label>
						<div class="selectable-row text-body-2">
							<span>{{ $t('action_select_all') }}</span>
						</div>
					</template>
				</v-checkbox>
				<v-divider class="my-1" />
				<v-checkbox
					v-for="option in options"
					:key="option.value"
					v-model="localSelection"
					:value="option.value"
					density="compact"
					hide-details
					color="primary-green"
					class="px-2 w-100"
				>
					<template v-slot:label>
						<div class="selectable-row">
							<span class="text-body-2">{{ option.text }}</span>
							<v-icon size="14" color="grey">{{ option.icon }}</v-icon>
						</div>
					</template>
				</v-checkbox>
			</v-card-text>
			<v-divider />
			<v-card-actions>
				<v-spacer />
				<v-btn variant="text" @click="handleReset">{{ $t('core_dashboard_reset') }}</v-btn>
				<v-btn variant="text" color="primary" @click="handleApply">{{ $t('action_apply') }}</v-btn>
			</v-card-actions>
		</v-card>
	</v-menu>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { StatOption, DriverStatKey } from '@/rennovation/planning-view/types/components/types.ts';

const props = defineProps<{ options: StatOption[]; modelValue: DriverStatKey[] }>();
const emit = defineEmits(['apply', 'reset', 'update:modelValue']);

const menuOpen = ref(false);

const localSelection = ref<DriverStatKey[]>([...props.modelValue]);

const committedSelection = ref<DriverStatKey[]>([...props.modelValue]);

watch(
	() => props.modelValue,
	(newVal) => {
		localSelection.value = [...newVal];
		committedSelection.value = [...newVal];
	}
);

watch(menuOpen, (isOpen) => {
	if (isOpen) {
		localSelection.value = [...committedSelection.value];
	}
});

const isAllSelected = computed({
	get: () => localSelection.value.length === props.options.length,
	set: (val) => (localSelection.value = val ? props.options.map((o) => o.value) : []),
});
const isIndeterminate = computed(() => localSelection.value.length > 0 && localSelection.value.length < props.options.length);

const handleReset = () => {
	localSelection.value = [];
	committedSelection.value = [];
	emit('reset');
	menuOpen.value = false;
};

const handleApply = () => {
	committedSelection.value = [...localSelection.value];
	emit('apply', localSelection.value);
	menuOpen.value = false;
};
</script>

<style lang="scss" scoped>
.cursor-pointer {
	cursor: pointer;
}

.selectable-row {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	width: 100%;
}

:deep .v-card-text {
	padding-right: 10px;
	padding-top: 6px;
	padding-left: 0;
}

:deep .v-checkbox {
	font-size: 14px !important;
}

:deep(.v-checkbox .v-selection-control) {
	width: 100%;
}

:deep(.v-checkbox .v-label) {
	width: 100%;
	opacity: 1;
}
</style>
